/**
 * The reference side of the route tool.
 *
 * Everything here is something an operator reads while doing something else:
 * following directions, checking what is ahead, watching a run. Previously it
 * all lived in the same scrolling column as the editing controls, collapsed by
 * default and cleared whenever the route changed, which meant the answers were
 * hardest to reach exactly when they were being used. Splitting reference from
 * editing gives each half a fixed place, and tabs mean opening one no longer
 * pushes the others off the screen.
 *
 * The dock renders whether or not the planner is open. That is deliberate: a
 * mission's stored findings are as useful to the crew running it as they were
 * to the person who planned it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Box, Divider, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import DescriptionIcon from '@mui/icons-material/Description';
import FenceIcon from '@mui/icons-material/Fence';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PlaceIcon from '@mui/icons-material/Place';
import SensorsIcon from '@mui/icons-material/Sensors';
import RadarIcon from '@mui/icons-material/Radar';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import { PanelEdgeTab } from '../../layout/PanelEdgeTab';
import { DockTabHelp } from '../../missions/DockTabHelp';
import { ManeuverStep, RouteAsset, RouteEvent, RouteRiskSegment, RouteRiskSettings } from '../types';
import { RouteIntel, tabCounts } from './intel';
import DirectionsTab from './DirectionsTab';
import PlacesTab from './PlacesTab';
import EventsTab from './EventsTab';
import WeatherTab from './WeatherTab';
import CorridorTab from './CorridorTab';

const GOLD = '#f5b942';

const MIN_WIDTH = 280;
const MAX_WIDTH = 620;
const RAIL_WIDTH = 40;

export type DockTabId = 'sentinel' | 'activity' | 'chat' | 'follow' | 'signals' | 'photos' | 'dwells' | 'areas' | 'line' | 'along' | 'report';

const LEGACY_TAB: Record<string, DockTabId> = {
  directions: 'line',
  corridor: 'line',
  places: 'along',
  events: 'along',
  followers: 'follow',
  live: 'sentinel',
};

const TAB_ICON: Record<DockTabId, React.ReactElement> = {
  sentinel: <AutoAwesomeIcon sx={{ fontSize: 17 }} />,
  activity: <SensorsIcon sx={{ fontSize: 17 }} />,
  chat: <ChatBubbleOutlineIcon sx={{ fontSize: 17 }} />,
  follow: <RadarIcon sx={{ fontSize: 17 }} />,
  signals: <WifiTetheringIcon sx={{ fontSize: 17 }} />,
  photos: <PhotoCameraIcon sx={{ fontSize: 17 }} />,
  dwells: <HourglassEmptyIcon sx={{ fontSize: 17 }} />,
  areas: <FenceIcon sx={{ fontSize: 17 }} />,
  line: <AltRouteIcon sx={{ fontSize: 17 }} />,
  along: <PlaceIcon sx={{ fontSize: 17 }} />,
  report: <DescriptionIcon sx={{ fontSize: 17 }} />,
};

const TAB_LABEL: Record<DockTabId, string> = {
  sentinel: 'Sentinel',
  activity: 'Activity',
  chat: 'Chat',
  follow: 'Follows',
  signals: 'Signals',
  photos: 'Photos',
  dwells: 'Dwells',
  areas: 'Fences',
  line: 'Route',
  along: 'Info',
  report: 'Report',
};

/**
 * A count over an icon, for the collapsed rail.
 *
 * Zero is shown only once the section has actually run — before that there is
 * no difference between "none found" and "not looked", and a badge reading 0
 * would claim the first.
 */
const CountBadge: React.FC<{
  count: number | null;
  urgent?: boolean;
  children: React.ReactNode;
}> = ({
  count, urgent, children,
}) => {
  if (count == null) return <>{children}</>;
  const shown = count > 9 ? '9+' : count;
  return (
    <Badge
      badgeContent={shown}
      showZero
      sx={{
        '& .MuiBadge-badge': {
          // Stay on the icon. Hanging right of a 40px rail is what got
          // clipped by the window edge.
          right: 0,
          top: 0,
          height: 14,
          minWidth: 14,
          fontSize: '0.58rem',
          fontWeight: 700,
          padding: '0 3px',
          color: urgent && count > 0 ? '#210a09' : '#111',
          bgcolor: urgent && count > 0
            ? '#ef5350'
            : count > 0
              ? 'rgba(232,235,238,0.85)'
              : 'rgba(255,255,255,0.25)',
        },
      }}
    >
      {children}
    </Badge>
  );
};

/**
 * A tab's name and its count, side by side.
 *
 * Deliberately not the overlaid badge used on the rail. Five tabs share a
 * panel that starts at 280px, and a badge hung off the right of each label sat
 * on top of the next tab's first letter — "Events" reading as "Event(" is
 * worse than no count at all.
 */
const TabLabel: React.FC<{ label: string; count: number | null; urgent?: boolean }> = ({
  label, count, urgent,
}) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
    {label}
    {count != null && (
      <Box
        component="span"
        sx={{
          px: 0.4,
          borderRadius: 0.5,
          fontSize: '0.55rem',
          lineHeight: 1.5,
          fontWeight: 800,
          color: urgent && count > 0 ? '#210a09' : '#111',
          bgcolor: urgent && count > 0
            ? '#ef5350'
            : count > 0
              ? 'rgba(232,235,238,0.85)'
              : 'rgba(255,255,255,0.25)',
        }}
      >
        {count > 9 ? '9+' : count}
      </Box>
    )}
  </Box>
);

export interface RouteReferenceDockProps {
  intel: RouteIntel;
  hasRoute: boolean;
  hasDepartAt?: boolean;
  /** Live geometry for the elevation chart. */
  geometry?: GeoJSON.LineString | null;
  /** Persistent last-update / open-alert strip above every tab. */
  banner?: React.ReactNode;
  /** Sentinel conversation. First tab when a mission is open. */
  sentinel?: React.ReactNode;
  /** Activity waterfall. Sits after Sentinel. */
  activity?: React.ReactNode;
  /** Crew thread; omitted when there is no mission. */
  chat?: React.ReactNode;
  /** Open alerts, for the Activity tab badge. */
  openAlerts?: number | null;
  /** Watched-area log. */
  areas?: React.ReactNode;
  areaCount?: number | null;
  /** Field-report gallery. */
  photos?: React.ReactNode;
  photoCount?: number | null;
  /** Stay-points during the run. */
  dwells?: React.ReactNode;
  dwellCount?: number | null;
  /** Jump to Sentinel when a run starts. */
  preferLive?: boolean;
  /** Open this tab when `requestTabKey` changes (Photos from the briefing). */
  requestTab?: DockTabId | null;
  requestTabKey?: number;
  /** Rendered in the Follows tab; omitted when there is no mission to watch. */
  follow?: React.ReactNode;
  /** Co-travellers at confirmed or above, for the Follows tab badge. */
  pressingFollows?: number | null;
  /** On-request RF digest. Badge is multi-location count after a scan. */
  signals?: React.ReactNode;
  signalCount?: number | null;
  /** Sentinel findings and the run report. */
  report?: React.ReactNode;
  reportCount?: number | null;
  /**
   * Namespaces the remembered width, open state and tab.
   *
   * A dialog and a full page have very different amounts of room, so one
   * shared width means sizing the dock comfortably in one place leaves it
   * unusable in the other.
   */
  storageKey?: string;
  onSelectRoute?: (index: number) => void;
  /** Keeps one of the offered routes as the mission's pre-vetted fallback. */
  onSetAlternate?: (index: number) => void;
  alternateIndex?: number | null;
  primaryName?: string;
  alternateName?: string;
  onHoverRoute?: (index: number | null) => void;
  onFlyToStep?: (step: ManeuverStep) => void;
  onHoverElevation?: (point: { longitude: number; latitude: number } | null) => void;
  onFocusAsset?: (asset: RouteAsset) => void;
  onFocusEvent?: (event: RouteEvent) => void;
  onFocusAlert?: (latitude: number, longitude: number) => void;
  onFocusSegment?: (segment: RouteRiskSegment) => void;
  placesRadiusM?: number;
  onPlacesRadiusChange?: (metres: number) => void;
  eventsCorridorM?: number;
  onEventsCorridorChange?: (metres: number) => void;
  eventsHorizonHours?: number;
  onEventsHorizonChange?: (hours: number) => void;
  eventsWindowStart?: string;
  eventsWindowEnd?: string;
  onEventsWindowStartChange?: (iso: string) => void;
  onEventsWindowEndChange?: (iso: string) => void;
  corridorSettings?: RouteRiskSettings;
  onCorridorSettingsChange?: (next: Partial<RouteRiskSettings>) => void;
}

const RouteReferenceDock: React.FC<RouteReferenceDockProps> = ({
  intel,
  hasRoute,
  hasDepartAt = false,
  geometry,
  banner,
  sentinel,
  activity,
  chat,
  openAlerts,
  areas,
  areaCount,
  photos,
  photoCount,
  dwells,
  dwellCount,
  preferLive,
  requestTab,
  requestTabKey,
  follow,
  pressingFollows,
  signals,
  signalCount,
  report,
  reportCount,
  storageKey = 'route-dock',
  onSelectRoute,
  onSetAlternate,
  alternateIndex,
  primaryName,
  alternateName,
  onHoverRoute,
  onFlyToStep,
  onHoverElevation,
  onFocusAsset,
  onFocusEvent,
  onFocusAlert,
  onFocusSegment,
  placesRadiusM,
  onPlacesRadiusChange,
  eventsCorridorM,
  onEventsCorridorChange,
  eventsHorizonHours,
  onEventsHorizonChange,
  eventsWindowStart,
  eventsWindowEnd,
  onEventsWindowStartChange,
  onEventsWindowEndChange,
  corridorSettings,
  onCorridorSettingsChange,
}) => {
  const widthKey = `${storageKey}-width`;
  const openKey = `${storageKey}-open`;
  const tabKey = `${storageKey}-tab`;

  const [open, setOpen] = useState(
    () => window.localStorage.getItem(openKey) !== 'false',
  );
  const [width, setWidth] = useState(() => {
    const saved = Number(window.localStorage.getItem(widthKey) || 0);
    return saved >= MIN_WIDTH && saved <= MAX_WIDTH ? saved : 360;
  });
  const [resizing, setResizing] = useState(false);
  const [tab, setTab] = useState<DockTabId>(() => {
    const raw = window.localStorage.getItem(tabKey) || '';
    return LEGACY_TAB[raw] || (raw as DockTabId) || 'line';
  });

  const tabs = useMemo<DockTabId[]>(() => {
    const ids: DockTabId[] = [];
    if (sentinel) ids.push('sentinel');
    if (activity) ids.push('activity');
    if (chat) ids.push('chat');
    if (follow) ids.push('follow');
    if (signals) ids.push('signals');
    if (photos) ids.push('photos');
    if (dwells) ids.push('dwells');
    if (areas) ids.push('areas');
    ids.push('line', 'along');
    if (report) ids.push('report');
    return ids;
  }, [sentinel, activity, chat, follow, signals, photos, dwells, areas, report]);

  // A tab that no longer exists would render nothing at all, which looks like a
  // broken panel rather than a missing mission.
  useEffect(() => {
    if (!tabs.includes(tab)) setTab(tabs[0] || 'line');
  }, [tabs, tab]);

  useEffect(() => window.localStorage.setItem(tabKey, tab), [tabKey, tab]);
  useEffect(() => window.localStorage.setItem(openKey, String(open)), [openKey, open]);

  const hasSentinel = !!sentinel;
  useEffect(() => {
    if (preferLive && hasSentinel) setTab('sentinel');
  }, [preferLive, hasSentinel]);

  useEffect(() => {
    if (!requestTab || requestTabKey == null) return;
    if (tabs.includes(requestTab)) {
      setTab(requestTab);
      setOpen(true);
    }
  }, [requestTab, requestTabKey, tabs]);

  useEffect(() => {
    if (!resizing) return undefined;
    const previousSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    let latest = width;
    const onMove = (e: MouseEvent) => {
      // Dragging the left edge of a right-hand panel widens it as the pointer
      // moves left, so the delta is measured from the window edge inwards.
      latest = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - e.clientX));
      setWidth(latest);
    };
    const onUp = () => {
      setResizing(false);
      window.localStorage.setItem(widthKey, String(latest));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.userSelect = previousSelect;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizing]);

  const counts = useMemo(() => tabCounts(intel), [intel]);
  const badgeFor = (id: DockTabId): number | null => {
    switch (id) {
      case 'line': return counts.corridor ?? counts.directions;
      case 'along':
        if (counts.places == null && counts.events == null && counts.weather == null) return null;
        return (counts.places || 0) + (counts.events || 0) + (counts.weather || 0);
      case 'activity': return openAlerts ?? null;
      case 'areas': return areaCount ?? null;
      case 'photos': return photoCount ?? null;
      case 'dwells': return dwellCount ?? null;
      case 'follow': return pressingFollows ?? null;
      case 'signals': return signalCount ?? null;
      case 'report': return reportCount ?? null;
      default: return null;
    }
  };

  const totalHidden = useMemo(() => {
    if (open) return 0;
    return tabs.reduce((sum, id) => sum + Math.max(0, badgeFor(id) || 0), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tabs, counts, openAlerts, areaCount, photoCount, dwellCount, pressingFollows, signalCount, reportCount]);

  if (!open) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: RAIL_WIDTH,
          flexShrink: 0,
          height: '100%',
          borderLeft: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1.25,
          gap: 1.25,
          overflow: 'visible',
        }}
      >
        <PanelEdgeTab side="left" title="Open reference panel" onClick={() => setOpen(true)}>
          <Badge
            variant="dot"
            invisible={totalHidden === 0}
            sx={{ '& .MuiBadge-badge': { bgcolor: GOLD, right: 1, top: 1 } }}
          >
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          </Badge>
        </PanelEdgeTab>
        {tabs.map((id) => (
          <Tooltip key={id} title={TAB_LABEL[id]} placement="left">
            <Box
              role="button"
              tabIndex={0}
              onClick={() => { setTab(id); setOpen(true); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { setTab(id); setOpen(true); } }}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                color: tab === id ? GOLD : 'text.secondary',
                '&:hover': { color: GOLD },
              }}
            >
              <CountBadge count={badgeFor(id)} urgent={id === 'activity'}>
                {TAB_ICON[id]}
              </CountBadge>
            </Box>
          </Tooltip>
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        // Shrinkable, and the map beside it is given a real flex basis, so
        // when the window cannot hold both at their preferred size the two
        // give ground together. Pinned, the dock would take its full width
        // out of the map and leave a strip too narrow to plan a route on.
        display: { xs: 'none', md: 'flex' },
        position: 'relative',
        flex: `0 1 ${width}px`,
        minWidth: MIN_WIDTH,
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box
        onMouseDown={(e) => { e.preventDefault(); setResizing(true); }}
        sx={{
          width: 6,
          cursor: 'col-resize',
          flexShrink: 0,
          bgcolor: resizing ? 'rgba(245,185,66,0.55)' : 'transparent',
          '&:hover': { bgcolor: 'rgba(245,185,66,0.35)' },
        }}
      />
      <PanelEdgeTab side="left" title="Collapse" onClick={() => setOpen(false)}>
        <ChevronRightIcon sx={{ fontSize: 16 }} />
      </PanelEdgeTab>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          minHeight: 0,
          borderLeft: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {banner}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tabs
            value={tab}
            onChange={(_, next) => setTab(next)}
            variant="scrollable"
            // Shown only when the labels genuinely do not fit, which at the
            // dock's minimum width they sometimes will. Without them a narrowed
            // dock hides its last tab with no way to reach it.
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              flex: 1,
              minHeight: 34,
              '& .MuiTab-root': {
                minHeight: 34,
                minWidth: 0,
                px: 0.85,
                fontSize: '0.66rem',
                fontWeight: 700,
                letterSpacing: 0.2,
                textTransform: 'none',
              },
              '& .MuiTabs-scrollButtons': { width: 18 },
              '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.25 },
              '& .Mui-selected': { color: `${GOLD} !important` },
              '& .MuiTabs-indicator': { backgroundColor: GOLD, height: 2 },
            }}
          >
            {tabs.map((id) => (
              <Tab
                key={id}
                value={id}
                label={<TabLabel label={TAB_LABEL[id]} count={badgeFor(id)} urgent={id === 'activity'} />}
              />
            ))}
          </Tabs>
        </Box>
        <Divider />

        <Box sx={{
          flex: 1,
          minHeight: 0,
          overflow: tab === 'chat' || tab === 'sentinel' ? 'hidden' : 'auto',
          p: 1.25,
          display: tab === 'chat' || tab === 'sentinel' ? 'flex' : 'block',
          flexDirection: 'column',
        }}>
          {tab === 'line' && (
            <>
              <DirectionsTab
                section={intel.directions}
                units={intel.units}
                geometry={geometry}
                onSelectRoute={onSelectRoute}
                onSetAlternate={onSetAlternate}
                alternateIndex={alternateIndex}
                primaryName={primaryName}
                alternateName={alternateName}
                onHoverRoute={onHoverRoute}
                onFlyToStep={onFlyToStep}
                onHoverElevation={onHoverElevation}
              />
              <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <CorridorTab
                  section={intel.corridor}
                  units={intel.units}
                  hasRoute={hasRoute}
                  settings={corridorSettings}
                  onSettingsChange={onCorridorSettingsChange}
                  onFocusSegment={onFocusSegment}
                />
              </Box>
            </>
          )}
          {tab === 'along' && (
            <>
              <PlacesTab
                section={intel.places}
                units={intel.units}
                hasRoute={hasRoute}
                radiusM={placesRadiusM}
                onRadiusChange={onPlacesRadiusChange}
                onFocusAsset={onFocusAsset}
              />
              <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <EventsTab
                  section={intel.events}
                  units={intel.units}
                  hasRoute={hasRoute}
                  hasDepartAt={hasDepartAt}
                  corridorM={eventsCorridorM}
                  onCorridorChange={onEventsCorridorChange}
                  horizonHours={eventsHorizonHours}
                  onHorizonChange={onEventsHorizonChange}
                  windowStart={eventsWindowStart}
                  windowEnd={eventsWindowEnd}
                  onWindowStartChange={onEventsWindowStartChange}
                  onWindowEndChange={onEventsWindowEndChange}
                  onFocusEvent={onFocusEvent}
                />
              </Box>
              <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <WeatherTab
                  section={intel.weather}
                  hasRoute={hasRoute}
                  onFocusAlert={onFocusAlert}
                />
              </Box>
            </>
          )}
          {tab === 'sentinel' && (
            sentinel || (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Sentinel</Typography>
                  <DockTabHelp tab="sentinel" />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Open a mission to ask Sentinel.
                </Typography>
              </>
            )
          )}
          {tab === 'activity' && (
            activity || (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Activity</Typography>
                  <DockTabHelp tab="activity" />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Open a mission to watch the activity feed.
                </Typography>
              </>
            )
          )}
          {tab === 'chat' && chat}
          {tab === 'areas' && areas}
          {tab === 'photos' && photos}
          {tab === 'dwells' && dwells}
          {tab === 'follow' && follow}
          {signals && (
            <Box sx={{ display: tab === 'signals' ? 'block' : 'none' }}>
              {signals}
            </Box>
          )}
          {tab === 'report' && report}
        </Box>
      </Box>
    </Box>
  );
};

export default RouteReferenceDock;
